<!--
	@name app-conference-fit-for-the-future
	@description Fit For The Future page
	@date 2020/05/20
	@license no license
	@copywrite Answers In Retirement Limited
-->

<template>
	<div :component="$options.name">
		<div class="py-15 hero">
			<v-container fill-height>
				<v-row no-gutters class="py-10">
					<v-col cols="12" sm="12" class="pr-10">
						<div class="text-h3 text-lg-h2 white--text font-weight-black mb-4">
							Fit <span class="font-weight-bold">for the</span> Future
						</div>
						<div class="text-h4 text-lg-h4 white--text font-weight-medium text-uppercase" style="line-height: 42px;">
							Prepare to perform
						</div>
					</v-col>
				</v-row>
			</v-container>
		</div>

		<!-- About Us -->
		<div>
			<v-container class="py-8" :class="{ 'py-15': $vuetify.breakpoint.mdAndUp }" style="max-width: 1050px;">
				<div class="text-md-center d-flex align-center flex-column px-md-10">
					<p class="text-h6 text-md-h5 text-lg-h3 font-weight-bold mb-6" :style="{ 'line-height': $vuetify.breakpoint.lgAndUp ? '3.75rem' : 'auto' }">
						Helping advisers kickstart 2025 in the Later Life Lending market
					</p>
					<p class="text-h6 font-weight-regular mb-4">
						Our Fit for the Future event was held on January 16<sup>th</sup> 2025 at the iconic Allianz Stadium. Throughout the day, industry experts delivered insightful sessions around market opportunities, innovation to meet the evolving needs of over-50s customers, leveraging AI in Later Life Lending and more.
					</p>
					<p class="text-h6 font-weight-regular mb-0">
						As the leading platform for later life lending professionals, we remain committed to supporting our members and the Later Life Lending market with the tools and resources needed to navigate the market and for advisers to unlock their potential.
					</p>

					<div class="mt-15 mt-md-12 mx-n3">
						<v-btn :large="$vuetify.breakpoint.mdAndUp" target="_blank" color="#3871d8" dark class="mx-3" @click="handleCtaClick({ function: 'arrangeDemo', subject: 'Making the Complex Simple' })">
							request a callback
						</v-btn>
					</div>
				</div>
			</v-container>
		</div>

		<!-- Plenary 1 -->
		<conference-section :id="'plenary1'" :title="'Introduction'" :subtitle="'Market changes now and in the future'" :avatars="avatars1" vimeo="1053461981" @handle-cta-click="handleCtaClick">
			<template #copy>
				<slot>
					<p class="text-body-1 mb-4">
						An opening address covering the evolving needs of clients and the changes the market needs to make, in order to embrace the advice requirements of the future. Setting the scene for key considerations for the rest of the day and actions to be taken collaboratively beyond that.
					</p>
					<p class="text-body-1 font-weight-medium mb-0">
						Discover how Air can support you in meeting evolving client needs—request a call back to explore the next steps.
					</p>
					<div class="mt-6">
						<v-btn target="_blank" color="#3871d8" dark @click="handleCtaClick({ function: 'arrangeDemo', subject: 'Market changes now and in the future' })">
							request a callback
						</v-btn>
					</div>
				</slot>
			</template>
		</conference-section>

		<!-- Plenary 2 -->
		<conference-section :id="'plenary2'" :title="'Keynote'" :subtitle="'Market Evolution and Opportunities Ahead'" :avatars="avatars2" vimeo="1056338125" :reverse="true" @handle-cta-click="handleCtaClick">
			<template #copy>
				<slot>
					<p class="text-body-1 mb-4">
						Enlightening keynote from Hansen Lu, Senior Economist at Aviva, exploring anticipated shifts in the 2025 financial market. Gain insights into emerging opportunities and challenges that will shape the industry, setting the stage to strategise effectively for the year ahead.
					</p>
					<p class="text-body-1 font-weight-medium mb-0">
						Stay ahead of the curve with expert support from Air and our Ambassadors—connect with us today to navigate 2025's market shifts with confidence.
					</p>
					<div class="mt-6 mx-n3">
						<v-btn class="mx-3" target="_blank" color="#3871d8" dark @click="handleCtaClick({ function: 'arrangeDemo', subject: 'Market Evolution and Opportunities Ahead' })">
							request a callback
						</v-btn>
						<v-btn class="mx-3" target="_blank" color="#3871d8" outlined @click="handleCtaClick({ route: 'events?category=Lending%20Criteria%20Clinic' })">
							REGISTER FOR CRITERIA CLINICS
						</v-btn>
					</div>
				</slot>
			</template>
		</conference-section>

		<!-- Plenary 3 -->
		<conference-section :id="'plenary3'" :title="'Summit Panel'" :subtitle="'Innovating to Meet the Needs of the Over 50s'" :avatars="avatars3" vimeo="1056750477" @handle-cta-click="handleCtaClick">
			<template #copy>
				<slot>
					<p class="text-body-1 mb-4">
						Engage with senior leaders from lenders, networks, and firms as they discuss how the industry is responding to the evolving needs of clients over 50. Discover how product innovation and strategic initiatives are addressing this demographic and explore how you can leverage these trends in your own advisory practice.
					</p>
					<p class="text-body-1 font-weight-medium mb-0">
						Speak with Air to explore how we can help you to leverage industry trends and better support your clients over 50.
					</p>
					<div class="mt-6">
						<v-btn target="_blank" color="#3871d8" dark @click="handleCtaClick({ function: 'arrangeDemo', subject: 'Innovating to Meet the Needs of the Over 50s' })">
							request a callback
						</v-btn>
					</div>
				</slot>
			</template>
		</conference-section>

		<!-- Masterclass 1 -->
		<conference-section :id="'masterclass1'" :title="'Masterclass'" :subtitle="'Crafting the Holistic Adviser of the Future'" :avatars="avatars5" vimeo="1057422160" :reverse="true">
			<template #copy>
				<slot>
					<p class="text-body-1 mb-4">
						In this session the panel of experts discuss how to adopt a broader view of clients' needs—especially those over 50—by integrating comprehensive financial solutions and collaborating with specialists.
					</p>
					<p class="text-body-1 font-weight-medium mb-0">
						Connect with Air to explore how our market leading tools and Academy learning platform can support you in delivering comprehensive solutions for your clients.
					</p>
					<div class="mt-6 mx-n3">
						<v-btn class="mx-3" target="_blank" color="#3871d8" dark @click="handleCtaClick({ function: 'arrangeDemo', subject: 'Crafting the Holistic Adviser of the Future' })">
							request a callback
						</v-btn>
						<v-btn class="mx-3" target="_blank" color="#3871d8" outlined @click="handleCtaClick({ route: 'events?category=Training' })">
							JOIN A TRAINING WEBINAR
						</v-btn>
					</div>
				</slot>
			</template>
		</conference-section>

		<!-- Masterclass 2 -->
		<conference-section
			:id="'masterclass2'"
			:title="'Masterclass'"
			:subtitle="'Leveraging AI in Later Life Lending'"
			:avatars="avatars6"
			vimeo="1057423765"
			@handle-cta-click="handleCtaClick"
		>
			<template #copy>
				<slot>
					<p class="text-body-1 mb-4">
						This session covers how technology and AI are transforming advisory processes. Listen to practical guidance on integrating AI tools into your practice to enhance client relationships, streamline operations, stay competitive in a rapidly evolving market and understand how AI can be a game-changer for your business in 2025.
					</p>
					<p class="text-body-1 font-weight-medium mb-0">
						Looking to streamline your business processes? Discover how Air's best-in-class sourcing platform, WriteRoute fact-find tool, Navigator tool can keep you ahead in 2025.
					</p>
					<div class="mt-6 mx-n3">
						<v-btn class="mx-3" target="_blank" color="#3871d8" dark @click="handleCtaClick({ function: 'arrangeDemo', subject: 'Leveraging AI in Later Life Lending' })">
							request a callback
						</v-btn>
						<v-btn class="mx-3" target="_blank" color="#3871d8" outlined @click="handleCtaClick({ route: 'events?category=Training' })">
							JOIN A TRAINING WEBINAR
						</v-btn>
					</div>
				</slot>
			</template>
		</conference-section>

		<div v-show="displayCountdown">
			<v-divider inset />
			<v-container class="py-8" :class="{ 'py-15': $vuetify.breakpoint.mdAndUp }">
				<div class="text-md-center d-flex align-center flex-column px-md-10">
					<div>
						<p class="text-h6 text-md-h5 text-lg-h4 font-weight-bold mb-6">
							What's next?
						</p>
						<div class="wrap">
							<div class="countdown countdown-static">
								<div class="bloc-time days" :data-init-value="days">
									<span class="count-title">Days</span>

									<div class="figure days days-1">
										<span class="top">0</span>
										<span class="top-back">
											<span>0</span>
										</span>
										<span class="bottom">0</span>
										<span class="bottom-back">
											<span>0</span>
										</span>
									</div>

									<div class="figure days days-2">
										<span class="top">0</span>
										<span class="top-back">
											<span>0</span>
										</span>
										<span class="bottom">0</span>
										<span class="bottom-back">
											<span>0</span>
										</span>
									</div>
								</div>

								<div class="bloc-time hours" :data-init-value="hours">
									<span class="count-title">Hours</span>

									<div class="figure hours hours-1">
										<span class="top">0</span>
										<span class="top-back">
											<span>0</span>
										</span>
										<span class="bottom">0</span>
										<span class="bottom-back">
											<span>0</span>
										</span>
									</div>

									<div class="figure hours hours-2">
										<span class="top">0</span>
										<span class="top-back">
											<span>0</span>
										</span>
										<span class="bottom">0</span>
										<span class="bottom-back">
											<span>0</span>
										</span>
									</div>
								</div>

								<div class="bloc-time min" :data-init-value="minutes">
									<span class="count-title">Minutes</span>

									<div class="figure min min-1">
										<span class="top">0</span>
										<span class="top-back">
											<span>0</span>
										</span>
										<span class="bottom">0</span>
										<span class="bottom-back">
											<span>0</span>
										</span>
									</div>

									<div class="figure min min-2">
										<span class="top">0</span>
										<span class="top-back">
											<span>0</span>
										</span>
										<span class="bottom">0</span>
										<span class="bottom-back">
											<span>0</span>
										</span>
									</div>
								</div>

								<div class="bloc-time sec" :data-init-value="seconds">
									<span class="count-title">Seconds</span>

									<div class="figure sec sec-1">
										<span class="top">0</span>
										<span class="top-back">
											<span>0</span>
										</span>
										<span class="bottom">0</span>
										<span class="bottom-back">
											<span>0</span>
										</span>
									</div>

									<div class="figure sec sec-2">
										<span class="top">0</span>
										<span class="top-back">
											<span>0</span>
										</span>
										<span class="bottom">0</span>
										<span class="bottom-back">
											<span>0</span>
										</span>
									</div>
								</div>
							</div>
						</div>
						<p class="text-body-1 bypass--line-height--override text-md-h6 font-weight-regular mb-0">
							The next Air event is <span class="font-weight-bold">{{ nextEvent.name }}</span> on <span class="font-weight-bold">{{ eventDateTime }}</span>.
							<br />
							For details of upcoming events and training, <a @click="$router.push('/events')">visit our events page</a>.
						</p>
					</div>
				</div>
			</v-container>
		</div>

		<div id="contact" ref="contact" class="header--waves scroll-margin--fixed-header-offset">
			<!--Content before waves-->
			<div class="inner-header flex">
				<v-container class="py-8" :class="{ 'py-15': $vuetify.breakpoint.mdAndUp }">
					<div v-if="!success">
						<p class="white--text text-center text-h6 text-md-h5 text-lg-h4 font-weight-bold mb-6">
							Unlock your potential with Air.
						</p>
						<p class="white--text text-body-1 bypass--line-height--override text-md-h6 font-weight-regular text-center mb-10">
							Discover more opportunities with the <a class="font-weight-black" @click="$router.push('/navigator')">Air Navigator Tool</a>,
							helping you to identify the right later life lending or residential mortgage options for your clients.
							Plus, elevate your expertise with <a class="font-weight-black" @click="$router.push('/air-academy')">Air Academy</a>,
							offering courses and resources to support your professional growth. <span class="font-weight-black">Request a call back to learn more!</span>
						</p>
					</div>
					<div class="text-md-center d-flex align-center flex-column">
						<div class="grey lighten-4 pa-8 rounded elevation-6" style="max-width: 700px;">
							<v-fade-transition>
								<div v-if="success">
									<p class="text-h6 text-md-h5 text-lg-h4 font-weight-bold mb-4 mx-auto" style="max-width: 500px;">
										Thank you for reaching out!
									</p>
									<p class="text-body-1 bypass--line-height--override text-md-h6 font-weight-regular mb-6 mx-auto" style="max-width: 500px;">
										Your email has been sent. We'll get back to you shortly. In the meantime, check out <a class="ga--trigger ga--campaign-click ga--events" @click="$router.push('/events')">upcoming events</a> or read more <a class="ga--trigger ga--campaign-click ga--academy" @click="$router.push('/air-academy')">about us</a>.
									</p>
									<v-icon color="success" size="60">
										mdi-check-circle
									</v-icon>
								</div>
							</v-fade-transition>
							<template v-if="!success">
								<validation-observer ref="observer">
									<v-form @submit.prevent="submit">
										<v-row>
											<v-col cols="12" md="6">
												<validation-provider v-slot="{ errors }" name="First Name" rules="required">
													<v-text-field v-model="firstName" :disabled="disabled" :error-messages="errors" label="First Name" :hide-details="!errors?.length" filled />
												</validation-provider>
											</v-col>
											<v-col cols="12" md="6">
												<validation-provider v-slot="{ errors }" name="Last Name" rules="required">
													<v-text-field v-model="lastName" :disabled="disabled" :error-messages="errors" label="Last Name" :hide-details="!errors?.length" filled />
												</validation-provider>
											</v-col>
											<v-col cols="12">
												<validation-provider v-slot="{ errors }" name="Firm Name" rules="required">
													<v-text-field v-model="firmName" :disabled="disabled" :error-messages="errors" label="Firm Name" :hide-details="!errors?.length" filled />
												</validation-provider>
											</v-col>
											<v-col cols="12">
												<validation-provider v-slot="{ errors }" name="FCA Number" rules="required|numeric|min:6|max:7">
													<v-text-field v-model="fcaNumber" :disabled="disabled" :error-messages="errors" label="FCA Number" :hide-details="!errors?.length" filled />
												</validation-provider>
											</v-col>
											<v-col cols="12">
												<validation-provider v-slot="{ errors }" name="Email" rules="required|email">
													<v-text-field v-model="email" :disabled="disabled" :error-messages="errors" label="Email" :hide-details="!errors?.length" filled />
												</validation-provider>
											</v-col>
											<v-col cols="12">
												<validation-provider v-slot="{ errors }" name="Phone" rules="required|numeric">
													<v-text-field v-model="phone" :disabled="disabled" :error-messages="errors" label="Phone" :hide-details="!errors?.length" filled />
												</validation-provider>
											</v-col>
											<v-col cols="12">
												<v-btn class="ga--trigger ga--campaign-click ga--form-submit white--text" color="#3871d8" :disabled="disabled" :loading="loading" type="submit" large block>
													Submit for a call back
												</v-btn>
											</v-col>
										</v-row>
									</v-form>
								</validation-observer>
							</template>
						</div>
					</div>
				</v-container>
			</div>

			<!--Waves Container-->
			<div>
				<svg
					class="waves"
					xmlns="http://www.w3.org/2000/svg"
					xmlns:xlink="http://www.w3.org/1999/xlink"
					viewBox="0 24 150 28"
					preserveAspectRatio="none"
					shape-rendering="auto"
				>
					<defs>
						<path id="gentle-wave" d="M-160 44c30 0 58-18 88-18s 58 18 88 18 58-18 88-18 58 18 88 18 v44h-352z" />
					</defs>
					<g class="parallax">
						<use xlink:href="#gentle-wave" x="48" y="0" fill="rgba(255,255,255,0.7" />
						<use xlink:href="#gentle-wave" x="48" y="3" fill="rgba(255,255,255,0.5)" />
						<use xlink:href="#gentle-wave" x="48" y="5" fill="rgba(255,255,255,0.3)" />
						<use xlink:href="#gentle-wave" x="48" y="7" fill="#fff" />
					</g>
				</svg>
			</div>
			<!--Waves end-->
		</div>
		<div class="body--waves d-flex" />

		<common-dialog-login ref="login" @login-success="loginSuccess" />
	</div>
</template>

<script>
	import { mapActions, mapGetters } from 'vuex';
	import { orderBy } from 'lodash';
	import { gsap, Quart, Back } from 'gsap';
	import ConferenceSection from '@/component/app/air-later-life/conference/fit-for-the-future/section';
	import CommonDialogLogin from '@/component/common/dialog/login';
	import { ValidationObserver, ValidationProvider } from 'vee-validate';
	import { EventBus, ElementTools } from '@/utils';

	export default {
		name: 'app-conference',

		components: {
			ConferenceSection,
			CommonDialogLogin,
            ValidationObserver,
            ValidationProvider
		},

		data() {
			return {
				auth: null,
				displayCountdown: false,
				days: null,
				hours: null,
				minutes: null,
				seconds: null,
				total_seconds: null,
				gridCategories: [],
				avatars1: [
					{
						name: 'Will Hale',
						position: 'Air Group',
						image: `${process.env.VUE_APP_S3_AIR_ASSETS_URL}/manager_documents/0c975b3e-d213-4676-a379-06fc49c15cb8.png`
					},
					{
						name: 'Jim Boyd',
						position: 'ERC',
						image: `${process.env.VUE_APP_S3_AIR_ASSETS_URL}/manager_documents/42fc3d68-8a0d-4236-891b-40d0cdc11684.png`
					}
				],
				avatars2: [
					{
						name: 'Hansen Lu',
						position: 'Aviva',
						image: `${process.env.VUE_APP_S3_AIR_ASSETS_URL}/manager_documents/956ce64b-4961-4446-951e-8165b7e429fb.png`
					}
				],
				avatars3: [
					{
						name: 'Paul Glynn',
						position: 'Air',
						image: `${process.env.VUE_APP_S3_AIR_ASSETS_URL}/manager_documents/b9415cf2-5671-43eb-a99a-770669ddd2e1.png`
					},
					{
						name: 'Colin Bell',
						position: 'Perenna',
						image: `${process.env.VUE_APP_S3_AIR_ASSETS_URL}/manager_documents/ef91d026-e21b-4951-9fde-48cd0fd37e12.png`
					},
					{
						name: 'Kelly Melville-Kelly',
						position: 'ERC',
						image: `${process.env.VUE_APP_S3_AIR_ASSETS_URL}/manager_documents/54badcb4-611d-422c-9d2c-72b927f7e73e.png`
					},
					{
						name: 'Dave Harris',
						position: 'more2life',
						image: `${process.env.VUE_APP_S3_AIR_ASSETS_URL}/manager_documents/bcad3879-2c87-4371-91e0-00fa69ff52ad.png`
					},
					{
						name: 'Nick Flynn',
						position: 'Canada Life',
						image: `${process.env.VUE_APP_S3_AIR_ASSETS_URL}/manager_documents/c6987ab7-09de-44ba-859c-397a32b3078d.png`
					},
					{
						name: 'Chris Flowers',
						position: 'Royal London Equity Release',
						image: `${process.env.VUE_APP_S3_AIR_ASSETS_URL}/manager_documents/f788475d-feae-4a97-bc24-a48d5fd58cc8.png`
					},
					{
						name: 'Will Hale',
						position: 'Air Group',
						image: `${process.env.VUE_APP_S3_AIR_ASSETS_URL}/manager_documents/0c975b3e-d213-4676-a379-06fc49c15cb8.png`
					}
				],
				avatars5: [
					{
						name: 'Dan Holden',
						position: 'Air',
						image: `${process.env.VUE_APP_S3_AIR_ASSETS_URL}/manager_documents/847c62e2-07e9-4896-9254-bb43e17f8472.png`
					},
					{
						name: 'John Somerville',
						position: 'LIBF',
						image: `${process.env.VUE_APP_S3_AIR_ASSETS_URL}/manager_documents/6cb1e2ed-42be-4bfe-8dfc-3398deaf438e.png`
					},
					{
						name: 'Charlotte Allen',
						position: 'Key Group',
						image: `${process.env.VUE_APP_S3_AIR_ASSETS_URL}/manager_documents/6e6aef5c-f6fd-47ec-b396-45d0bf5326f9.png`
					},
					{
						name: 'Ben Allen',
						position: 'TRMN',
						image: `${process.env.VUE_APP_S3_AIR_ASSETS_URL}/manager_documents/ce196bad-37a2-47ea-9522-cc7a8d5c5c52.png`
					},
					{
						name: 'David Forsdyke',
						position: 'Knight Frank Finance',
						image: `${process.env.VUE_APP_S3_AIR_ASSETS_URL}/manager_documents/1a08ed19-b07f-4bb9-b080-3f29f03dee86.png`
					}
				],
				avatars6: [
					{
						name: 'Nick Birdseye',
						position: 'Legal & General',
						image: `${process.env.VUE_APP_S3_AIR_ASSETS_URL}/manager_documents/276b34b4-51ea-45b8-b333-0dbc0095a768.png`
					},
					{
						name: 'Darren Arulvasagam',
						position: 'Legal & General',
						image: `${process.env.VUE_APP_S3_AIR_ASSETS_URL}/manager_documents/7c9db627-6599-46a3-aec3-237135ddd2de.png`
					},
					{
						name: 'Marie Catch',
						position: 'Ignite',
						image: `${process.env.VUE_APP_S3_AIR_ASSETS_URL}/manager_documents/403a5373-584a-47bd-999f-e6fc8ccdc344.png`
					},
					{
						name: 'Matt Stirland',
						position: 'Maple Corner',
						image: `${process.env.VUE_APP_S3_AIR_ASSETS_URL}/manager_documents/15ec1c4c-e1b8-4c3a-8238-9bad3e9fce5a.png`
					},
					{
						name: 'Alistair Morrison',
						position: 'Air',
						image: `${process.env.VUE_APP_S3_AIR_ASSETS_URL}/manager_documents/d38d0dc0-7899-4035-938f-cd1b6cbb08e9.png`
					},
					{
						name: 'Steve Humphries',
						position: 'Mortgage Advice Bureau',
						image: `${process.env.VUE_APP_S3_AIR_ASSETS_URL}/manager_documents/48888a6d-5c69-41f6-b402-355155173990.png`
					}
				],
				avatars7: [
					{
						name: 'Damon Hill',
						position: '1996 Formula One World Champion',
						image: 'https://air-assets.airlaterlife.co.uk/cms/Damon+Hill.png'
					}
				],
                campaign: 'Fit for the Future Page',
                subject: null,
                firstName: '',
				lastName: '',
				firmName: '',
				fcaNumber: '',
				email: '',
				phone: '',
				message: '',
				loading: false,
				success: false,
				disabled: false
			};
		},

		computed: {
			...mapGetters('CmsEvent', ['events']),

			sortedEvents() {
				if (!this.events) return [];
				return orderBy(
					this.events,
					(e) => {
						return this.$moment(e.start).format('YYYYMMDD');
					},
					['asc']
				);
			},

			featuredEvents() {
				if (!this.events) return [];
				return this.sortedEvents.filter((e) => this.gridCategories.includes(e.category));
			},

			nextEvent() {
				let event = this.featuredEvents.slice(0, 1)[0];
				//if (!event) return { name: 'Test Event', end: '2025-03-22T09:00:00.000Z', start: '2025-03-22T09:00:00.000Z' };
				if (!event) return {};
				return event;
			},

			/**
			 * @name selectedEventEndTime
			 * @description - If on same day, change this to {start} - {endtime} rather than  {start} - {end}
			 * @return {String} time for selected event
			 */
			eventDateTime() {
				if (!this.nextEvent.start) return null;

				const start = this.$moment(String(this.nextEvent.start)).format('ddd, Do MMM YYYY h:mma');
				let end = this.$moment(String(this.nextEvent.end)).format('ddd, Do MMM YYYY h:mma');

				if (this.$moment(String(this.nextEvent.start)).isSame(this.$moment(String(this.nextEvent.end)), 'day')) {
					end = this.$moment(String(this.nextEvent.end)).format('h:mma');
				}

				return `${start} - ${end}`;
			}
		},

		async created() {
			this.auth = window.localStorage.getItem('air.authorization');
			this.comparisonDateTime = this.$moment();
			const options = await this.getSchemaOptions('event-category');
			this.gridCategories = options?.map(option => option.name);
			this.loadEventsList({ limit: 100, reset: true, period: 0 }).then(() => {
				this.$nextTick(() => {
					this.init();
				});
			});
		},

		methods: {
			...mapActions('CmsEvent', ['loadEventsList']),
			...mapActions('Option', ['getSchemaOptions']),
			...mapActions('ContactCampaign', ['submitContactCampaign']),

			init() {
				var t1 = new Date(this.nextEvent.start);
				var t2 = new Date();
				var dif = t1.getTime() - t2.getTime();
				if (dif < 0) return;

				this.total_seconds = Math.ceil(Math.abs(dif / 1000));
				var delta = this.total_seconds;

				this.days = Math.floor(delta / 86400);
				delta -= this.days * 86400;
				// calculate (and subtract) whole hours
				this.hours = Math.floor(delta / 3600);
				delta -= this.hours * 3600;

				// calculate (and subtract) whole minutes
				this.minutes = Math.floor(delta / 60);
				delta -= this.minutes * 60;

				// what's left is seconds
				this.seconds = Math.floor(delta % 60); // in theory the modulus is not required

				// Init countdown values
				this.values = {
					days: this.days,
					hours: this.hours,
					minutes: this.minutes,
					seconds: this.seconds
				};

				let selector = '.countdown-static';
				this.objel = document.querySelector(selector);

				// DOM
				this.obj = {
					days: this.objel.querySelectorAll('.bloc-time.days .figure'),
					hours: this.objel.querySelectorAll('.bloc-time.hours .figure'),
					minutes: this.objel.querySelectorAll('.bloc-time.min .figure'),
					seconds: this.objel.querySelectorAll('.bloc-time.sec .figure')
				};

				// Animate countdown to the end
				if (this.days < 100) {
					this.displayCountdown = true;
					this.count(this.obj);
				}
			},

			count(obj) {
				var that = this,
					$day_1 = obj.days[0],
					$day_2 = obj.days[1],
					$hour_1 = obj.hours[0],
					$hour_2 = obj.hours[1],
					$min_1 = obj.minutes[0],
					$min_2 = obj.minutes[1],
					$sec_1 = obj.seconds[0],
					$sec_2 = obj.seconds[1];

				if (this.countdown_interval) clearInterval(this.countdown_interval);

				this.countdown_interval = setInterval(() => {
					if (that.total_seconds > 0) {
						--that.values.seconds;

						if (that.values.minutes >= 0 && that.values.seconds < 0) {
							that.values.seconds = 59;
							--that.values.minutes;
						}

						if (that.values.hours >= 0 && that.values.minutes < 0) {
							that.values.minutes = 59;
							--that.values.hours;
						}

						if (that.values.days >= 0 && that.values.hours < 0) {
							that.values.hours = 23;
							--that.values.days;
						}

						// Days
						that.checkHour(that.values.days, $day_1, $day_2);
						// Hours
						that.checkHour(that.values.hours, $hour_1, $hour_2);
						// Minutes
						that.checkHour(that.values.minutes, $min_1, $min_2);
						// Seconds
						that.checkHour(that.values.seconds, $sec_1, $sec_2);

						--that.total_seconds;
					} else {
						this.displayCountdown = false;
						clearInterval(that.countdown_interval);
					}
				}, 1000);
			},

			animateFigure($el, value) {
				var $top = $el.querySelectorAll('.top'),
					$bottom = $el.querySelectorAll('.bottom'),
					$back_top = $el.querySelectorAll('.top-back'),
					$back_bottom = $el.querySelectorAll('.bottom-back');

				// Before we begin, change the back value
				$back_top[0].querySelectorAll('span')[0].innerHTML = value;

				// Also change the back bottom value
				$back_bottom[0].querySelectorAll('span')[0].innerHTML = value;

				// Then animate
				gsap.to($top[0], {
					rotationX: '-180deg',
					transformPerspective: 300,
					ease: Quart.easeOut,
					duration: 0.8,
					onComplete() {
						$top[0].innerHTML = value;
						$bottom[0].innerHTML = value;
						gsap.set($top[0], { rotationX: 0 });
					}
				});

				gsap.to($back_top[0], {
					rotationX: 0,
					transformPerspective: 300,
					ease: Back.easeOut,
					scaleX: 0.98,
					duration: 0.8,
					clearProps: 'all'
				});
			},

			checkHour(value, $el_1, $el_2) {
				var val_1 = value.toString().charAt(0),
					val_2 = value.toString().charAt(1),
					fig_1_value = $el_1.querySelectorAll('.top'),
					fig_2_value = $el_2.querySelectorAll('.top');

				if (value >= 10) {
					// Animate only if the figure has changed
					if (fig_1_value[0].innerHTML !== val_1) this.animateFigure($el_1, val_1);
					if (fig_2_value[0].innerHTML !== val_2) this.animateFigure($el_2, val_2);
				} else {
					// If we are under 10, replace first figure with 0
					if (fig_1_value[0].innerHTML !== '0') this.animateFigure($el_1, 0);
					if (fig_2_value[0].innerHTML !== val_1) this.animateFigure($el_2, val_1);
				}
			},

			login() {
				this.$refs.login.open('Title', `Are you sure...`);
			},

			loginSuccess() {
				this.auth = window.localStorage.getItem('air.authorization');
				this.$refs.login.close();
			},

			scrollToAnchorPoint(refName, subject = null) {
				if (subject) this.subject = subject;
                document.getElementById(refName).scrollIntoView({ behavior: 'smooth'});
            },

			handleCtaClick(cta) {
				if (cta.function) {
					let fnctn = cta.function;
					if (fnctn === 'arrangeDemo') this.scrollToAnchorPoint('contact', cta.subject || null);
					if (fnctn === 'openChat') EventBus.$emit('open-chat');
				} else if (cta.route) {
					let route = cta.route;
					if (this.auth && cta.authRoute) {
						route = cta.authRoute;
						this.$router.push(route);
					} else {
						this.$router.push(route);
					}
				}
			},

			submit() {
				this.$refs.observer.validate().then(async(valid) => {
					if (!valid) return false;

					this.success = false;
					this.loading = true;

					this.submitContactCampaign({
						firstName: this.firstName,
						lastName: this.lastName,
						firmName: this.firmName,
						fcaNumber: this.fcaNumber,
						email: this.email,
						phone: this.phone,
						campaign: this.campaign,
						subject: this.subject
					})
						.then(() => {
							this.success = true;
							this.resetForm();
						})
						.catch(() => ElementTools.fireNotification(this.$el, 'error', 'An error occurred. Please try again later.'))
						.finally(() => {
							this.subject = null;
							this.loading = false;
						});
				});
			},

			resetForm() {
				this.subject = null;
				this.firstName = '';
				this.lastName = '';
				this.firmName = '';
				this.fcaNumber = '';
				this.email = '';
				this.phone = '';
				this.disabled = true;
				this.$refs.observer.reset();
			}
		}
	};
</script>

<style lang="scss" scoped>
	#overlay {
		position: absolute;
		top: 0;
		left: 0;
		height: 100%;
		width: 100%;
		background-color: transparent;
		background-image: linear-gradient(340deg, #6464645c 45%, #1eb0e1 100%);
		opacity: 1;
		mix-blend-mode: multiply;
		transition: background 0.3s, border-radius 0.3s, opacity 0.3s;
	}

	.asphalt {
		position: relative;
		background-color: CCCCCC;

		.car {
			position: absolute;
			left: 0;
			top: 0;
			width: 100%;
			height: 100%;
			background-image: url($VUE_APP_S3_AIR_ASSETS_URL + '/cms/clipart4510703.png');
			background-repeat: no-repeat;
			background-position: -250% 50%;
			background-attachment: fixed;
			background-size: 20%;
			animation: animatedBackground 20s ease-in-out infinite;
			opacity: 0.4;
		}

		@keyframes animatedBackground {
			0% {
				background-position: -250% 50%;
			}
			25% {
				background-position: 250% 50%;
			}
			100% {
				background-position: 250% 50%;
			}
		}

		&:before {
			content: ' ';
			display: block;
			position: absolute;
			left: 0;
			top: 0;
			width: 100%;
			height: 100%;
			opacity: 0.15;
			background-image: url($VUE_APP_S3_AIR_ASSETS_URL + '/cms/asphalt-road-texture-dark-gray-color.jpg');
			background-repeat: no-repeat;
			background-position: 50% 0;
			background-size: cover;
			background-attachment: fixed;
		}

		&--content {
			position: relative;
		}
	}

	.curb {
		height: 20px;

		&--top {
			border-top: 3px solid #ffffff;
			box-shadow: 2px -5px 10px 2px rgba(0, 0, 0, 0.25);
			background: repeating-linear-gradient(45deg, $error, $error 100px, #ffffff 100px, #ffffff 200px);
		}

		&--bottom {
			border-bottom: 3px solid #ffffff;
			box-shadow: 2px 5px 10px 2px rgba(0, 0, 0, 0.25);
			background: repeating-linear-gradient(135deg, $error, $error 100px, #ffffff 100px, #ffffff 200px);
		}
	}

	@function tint($color, $percentage) {
		@return mix(white, $color, $percentage);
	}

	// Variables
	$lato: 'Roboto';
	$cardCol1: tint(rgb(51,80,217,216), 0%);
	$cardCol2: tint(rgb(51,80,217,216), 10%);

	// Countdown
	.countdown {
		width: 540px;
		margin: 0 auto 24px;
		display: flex;

		.bloc-time {
			text-align: center;
			flex-grow: 1;
			flex-shrink: 1;
			padding: 4px;
			flex-basis: 20%;

			&:last-child {
				margin-right: 0;
			}
		}

		.count-title {
			display: block;
			margin-bottom: 8px;
			font: normal 0.94em $lato;
			//color: #55acb8;
			//color: #f0acb8;
			text-transform: uppercase;
			font-weight: 500;
		}

		.figure {
			position: relative;
			float: left;
			height: 3.75rem;
			width: 46%;
			margin: 0 2%;
			background-color: $cardCol1;
			border-radius: 8px;
			box-shadow: 0 3px 4px 0 rgba(0, 0, 0, 0.25), inset 2px 4px 0 0 rgba(255, 255, 255, 0.08);

			&:last-child {
				margin-right: 0;
			}

			> span {
				position: absolute;
				left: 0;
				right: 0;
				margin: auto;
				font: normal 3rem $lato;
				line-height: 3.75rem;
				font-weight: 400;
				color: rgba(255, 255, 255, 0.6);
			}

			&.days {
				> span {
					color: rgba(255,255,255,0.9);
				}
			}

			.top,
			.bottom-back {
				&:after {
					content: '';
					position: absolute;
					z-index: -1;
					left: 0;
					bottom: 0;
					width: 100%;
					height: 100%;
					border-bottom: 1px solid rgba(0, 0, 0, 0.1);
				}
			}

			.top {
				z-index: 3;
				background-color: $cardCol2;
				transform-origin: 50% 100%;
				-webkit-transform-origin: 50% 100%;
				border-top-left-radius: 10px;
				border-top-right-radius: 10px;
				transform: perspective(200px);
			}

			.bottom {
				z-index: 1;

				&:before {
					content: '';
					position: absolute;
					display: block;
					top: 0;
					left: 0;
					width: 100%;
					height: 50%;
					background-color: rgba(0, 0, 0, 0.02);
				}
			}

			.bottom-back {
				z-index: 2;
				top: 0;
				height: 50%;
				overflow: hidden;
				background-color: $cardCol2;
				border-top-left-radius: 10px;
				border-top-right-radius: 10px;

				span {
					position: absolute;
					top: 0;
					left: 0;
					right: 0;
					margin: auto;
				}
			}

			.top,
			.top-back {
				height: 50%;
				overflow: hidden;
				backface-visibility: hidden;
			}

			.top-back {
				z-index: 4;
				bottom: 0;
				background-color: #fff;
				background-color: $cardCol1;
				-webkit-transform-origin: 50% 0;
				transform-origin: 50% 0;
				transform: perspective(200px) rotateX(180deg);
				border-bottom-left-radius: 10px;
				border-bottom-right-radius: 10px;

				span {
					position: absolute;
					top: -100%;
					left: 0;
					right: 0;
					margin: auto;
				}
			}
		}
	}

	.wrap {
		background-position: 50% 50%;
		background-size: cover;
	}

	.hero {
        position: relative;
        overflow: hidden;
        z-index: 1;
		height: 400px;
        border-bottom: 20px solid #1b78d8;
        background-position: 50% 50%;
        background-repeat: no-repeat;
        background-size: cover;
		background-image: url($VUE_APP_S3_AIR_ASSETS_URL + '/manager_documents/1ef736d9-b704-4ff7-adaa-533b0a5829a3.png');
    }

	.header--waves {
        border-top: 20px solid #1b78d8;
        background-position: 50% 50%;
        background-size: cover;
        background-attachment: fixed;
		background-image: linear-gradient(315deg, rgba(64,62,208, 1) 0%, rgba(63,41,155,1) 60%);
        position:relative;
    }

    .inner-header {
        width:100%;
        margin: 0;
        padding: 0;
    }

    .waves {
        position:relative;
        width: 100%;
        height:50px;
        margin-bottom:-7px; /*Fix for safari gap*/
        min-height:50px;
        max-height:50px;
    }

    .body--waves {
        position:relative;
        height:50px;
        text-align:center;
        background-color: white;
    }

	/* Animation */
	.parallax > use {
        animation: move-forever 25s cubic-bezier(.55,.5,.45,.5)     infinite;
    }
    .parallax > use:nth-child(1) {
        animation-delay: -2s;
        animation-duration: 7s;
    }
    .parallax > use:nth-child(2) {
        animation-delay: -3s;
        animation-duration: 10s;
    }
    .parallax > use:nth-child(3) {
        animation-delay: -4s;
        animation-duration: 13s;
    }
    .parallax > use:nth-child(4) {
        animation-delay: -5s;
        animation-duration: 20s;
    }
    @keyframes move-forever {
        0% {
        transform: translate3d(-90px,0,0);
        }
        100% {
            transform: translate3d(85px,0,0);
        }
    }
    /*Shrinking for mobile*/
    @media (max-width: 768px) {
        .waves {
            height:40px;
            min-height:40px;
        }
        .body--waves {
            height:30vh;
        }
    }
</style>
